import { addMinutes, isSameDay } from 'date-fns';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { DatePickerDateType } from 'ui';
import { useGetParsedStartTime } from './useGetParsedStartTime';
import { useGetStartDateTime } from './useGetStartDateTime';

export const useEventOverMidnightAlert = ({
  duration,
  startDate,
  startTime,
}: {
  duration?: number;
  startDate?: DatePickerDateType;
  startTime?: string;
}) => {
  const { t } = useTranslation();
  const getParsedStartTime = useGetParsedStartTime();
  const getStartDateTime = useGetStartDateTime();

  return useMemo(() => {
    if (!startDate || !startTime || !duration || !getParsedStartTime(startTime)) {
      return undefined;
    }

    const start = getStartDateTime({ startDate, startTime });
    const end = addMinutes(start, duration);

    if (isSameDay(start, end)) {
      return undefined;
    }

    return (
      <Trans
        components={{ strong: <strong /> }}
        i18nKey="Event over midnight: Event ends on {{date}} at {{time}} with duration of {{duration}}."
        values={{
          date: t('dateFormats.shortMonthDayYear', { date: end }),
          duration: duration < 60 ? `${duration} ${t('min')}` : t('hr', { count: duration / 60 }),
          time: t('dateFormats.time', { date: end }),
        }}
      />
    );
  }, [duration, getParsedStartTime, getStartDateTime, startDate, startTime, t]);
};
